import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiBrain, FiLeaf, FiHeart, FiTarget, FiShield, FiActivity, FiEye, FiRefreshCw, FiUsers, FiPlay } = FiIcons;

const Solution = () => {
  const rootSkills = [
    {
      name: "Impulse Control",
      description: "Build the pause between craving and action—the most critical recovery skill",
      icon: FiShield
    },
    {
      name: "Working Memory",
      description: "Hold your recovery goals in mind when cravings hit",
      icon: FiBrain
    },
    {
      name: "Attention Control",
      description: "Stay focused on what matters instead of getting hijacked by triggers",
      icon: FiTarget
    },
    {
      name: "Emotional Regulation",
      description: "Process feelings without needing to numb or escape them",
      icon: FiHeart
    },
    {
      name: "Stress Management",
      description: "Handle life's pressures without reaching for your substance of choice",
      icon: FiActivity
    },
    {
      name: "Interoception",
      description: "Learning to accurately identify physical cues (like stress, hunger, fatigue) so you can address them before they become triggers",
      icon: FiEye
    }
  ];

  const leafSkills = [
    {
      name: "Self-Monitoring",
      description: "Catch triggers, cravings, and risky situations before they derail you",
      icon: FiEye
    },
    {
      name: "Cognitive Flexibility",
      description: "Learning to adapt when plans fall apart without spiraling into \"all-or-nothing\" thinking",
      icon: FiRefreshCw
    },
    {
      name: "Problem-Solving",
      description: "Address life challenges without substances as your go-to solution",
      icon: FiBrain
    },
    {
      name: "Time Perspective",
      description: "Connect today's choices to your long-term recovery goals",
      icon: FiTarget
    },
    {
      name: "Social Skills",
      description: "Navigate relationships and peer pressure with confidence",
      icon: FiUsers
    },
    {
      name: "Initiation",
      description: "Overcoming the paralysis of starting new, healthy routines (exercise, meditation, job search)",
      icon: FiPlay
    }
  ];

  const flowerSkills = [
    {
      name: "Decision-Making",
      description: "Make choices aligned with your values, not your impulses",
      icon: FiTarget
    },
    {
      name: "Planning",
      description: "Build structured routines that support recovery and prevent relapse",
      icon: FiBrain
    },
    {
      name: "Goal-Setting",
      description: "Create meaningful life direction beyond just \"staying clean\"",
      icon: FiTarget
    },
    {
      name: "Self-Advocacy",
      description: "Communicate your needs and boundaries clearly",
      icon: FiUsers
    },
    {
      name: "Purpose Building",
      description: "Discover what makes life worth living without substances",
      icon: FiHeart
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-playfair font-bold text-gray-900 mb-6">
            Train Your Brain to Choose Recovery{' '}
            <span className="bg-gradient-to-r from-accent-magenta to-accent-purple bg-clip-text text-transparent">
              Every Time
            </span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto mb-16"
        >
          <div className="bg-accent-light/30 rounded-3xl p-8">
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Traditional recovery programs provide crucial community and coping mechanisms. But many still rely on a model that asks a neurologically compromised brain to simply "make better choices."
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              <strong>Executive Function Therapy is different.</strong> It's a proactive approach to rehabilitating the brain's management system.
            </p>
            <p className="text-xl font-semibold text-accent-magenta text-center">
              We don't just treat the addiction. We rebuild the executive function foundation that makes healthy choices feel natural again.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h3 className="text-2xl lg:text-3xl font-playfair font-bold text-gray-900 mb-4">
            Our Comprehensive Recovery Strengthening System
          </h3>
          <p className="text-gray-600 italic">Based on Sumpter Executive Function Model</p>
        </motion.div>

        {/* Root Skills */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-gradient-to-r from-accent-magenta to-accent-purple rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiBrain} className="w-6 h-6 text-white" />
            </div>
            <h4 className="text-2xl font-playfair font-bold text-gray-900">
              Root Skills (Cognitive Foundation)
            </h4>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rootSkills.map((skill, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-accent-light rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <SafeIcon icon={skill.icon} className="w-6 h-6 text-accent-magenta" />
                  <h5 className="font-semibold text-gray-900">{skill.name}</h5>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{skill.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Leaf Skills */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiLeaf} className="w-6 h-6 text-white" />
            </div>
            <h4 className="text-2xl font-playfair font-bold text-gray-900">
              Leaf Skills (Recovery Tools)
            </h4>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {leafSkills.map((skill, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.0 + index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-green-100 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <SafeIcon icon={skill.icon} className="w-6 h-6 text-green-600" />
                  <h5 className="font-semibold text-gray-900">{skill.name}</h5>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{skill.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Flower Skills */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-rose-500 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiHeart} className="w-6 h-6 text-white" />
            </div>
            <h4 className="text-2xl font-playfair font-bold text-gray-900">
              Flower Skills (Designing a Life Worth Staying Sober For)
            </h4>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {flowerSkills.map((skill, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.2 + index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-pink-100 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <SafeIcon icon={skill.icon} className="w-6 h-6 text-pink-600" />
                  <h5 className="font-semibold text-gray-900">{skill.name}</h5>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{skill.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Solution;